/**
 * Palette scope — mockup 07's "context from the INVOKER" made real.
 * A page, a record or a subgrid registers the list the user is looking at;
 * ⌘K opens scoped to it and ⌫ widens scope → global. Surfaces can also open
 * the palette PROGRAMMATICALLY with an explicit scope (the per-form / per-
 * subgrid ⌘ buttons). Module-level store (not context) so the shell's palette
 * and any page stay decoupled.
 */
import { useSyncExternalStore } from 'react'
import type { Leaf } from '@solum/rules'

export interface PaletteVerb {
  label: string
  run: () => void
  /** A server-named act this caller may not take now (or one already running):
   *  shown, not hidden — the same state the command button draws (#2849). */
  disabled?: boolean
  disabledReason?: string
  /** The governed act this verb launches, when it is one: a highlighted row's
   *  own copy of the same `none` act is not offered beside it (#2849). */
  tool?: string
}

export interface PaletteScope {
  /** entity key of the scoped list (e.g. crm_opportunity) */
  entity: string
  /** human label shown in the scope chip (e.g. "Pipeline") */
  label: string
  /**
   * Set when the scope IS one record rather than a list — the copilot reads it
   * so "what am I looking at?" has ONE answer for both surfaces (#484). Before
   * this, the copilot re-derived context by string-matching URL prefixes and
   * announced "Context: e." on the generic record route.
   */
  recordId?: string
  /** explicit item list (a subgrid's rows); absent ⇒ the palette lists the rows of `page` */
  items?: { id: string; label: string }[]
  /**
   * The scoped LIST's own page read (#2849, ROOT c.116770): the filters and
   * order the list reads its page with — an event filter among them — so ⌘K's
   * record pool is that list's rows, read through the same page contract. Only
   * a list lends one; a scope without it (a record, a workspace, a draft) has
   * no list behind it and the palette lists no records for it.
   *
   * `clientView` is a view the list evaluates IN THE BROWSER (a `$me` bind,
   * `empty`): the list lends its leaves and the palette applies them to its
   * page with the same `applyView`, so ⌘K never lists a row the list hides.
   */
  page?: {
    filters?: { key: string; value: string }[]; order?: string
    clientView?: { filters?: Leaf[]; sort_spec?: unknown[] }
  }
  /** live selection size — an active selection outranks the invoker (mockup 07) */
  selectionCount?: () => number
  /** the scoped create verb (kept for single-verb surfaces, e.g. the ghost row) */
  create?: PaletteVerb
  /** scoped command verbs — a record form offers ALL its related creates, a subgrid only its own */
  verbs?: PaletteVerb[]
  /** open one row of the scoped list (peek, record page, …) */
  open?: (id: string) => void
}

let current: PaletteScope | null = null
const subs = new Set<() => void>()

export function setPaletteScope(scope: PaletteScope | null) {
  current = scope
  subs.forEach((f) => f())
}

export function usePaletteScope(): PaletteScope | null {
  return useSyncExternalStore(
    (cb) => { subs.add(cb); return () => subs.delete(cb) },
    () => current,
  )
}

/* ── programmatic open (the ⌘ buttons) ── */
type OpenListener = (scope?: PaletteScope) => void
const openSubs = new Set<OpenListener>()

/** The shell registers its palette here. */
export function onPaletteOpen(cb: OpenListener): () => void {
  openSubs.add(cb)
  return () => openSubs.delete(cb)
}

/** Open the palette, optionally with an explicit invoker scope (outranks the page's). */
export function openPalette(scope?: PaletteScope) {
  if (scope) setPaletteScope(scope)
  openSubs.forEach((f) => f(scope))
}
